import { useQuery } from "@tanstack/react-query";
import { withNamespaces } from "react-i18next";
import { toast } from "react-toastify";
import { Card, CardBody, Col, Row, Spinner, Container, Button, UncontrolledTooltip } from "reactstrap";
import BootstrapTable from "react-bootstrap-table-next";
import overlayFactory from 'react-bootstrap-table2-overlay';
import paginationFactory, { 
    PaginationProvider, 
    PaginationListStandalone,
    SizePerPageDropdownStandalone,
    PaginationTotalStandalone
} from 'react-bootstrap-table2-paginator';
import { connect } from "react-redux";
import { EMPTY_LIST } from "src/common/constants";
import { useCallback, useMemo, useState } from "react";
import FilteringComponent from "./components/FilteringComponent";
import CommonService from "src/modules/3rd-party-management/apis/common/CommonService";
import EudrDeclarationService from "src/modules/3rd-party-management/apis/thirdparty/EudrDeclarationService";
import { DDSTypes } from "src/modules/3rd-party-management/constants";
import DateUtils from "src/services/utils/DateUtils";
import {
    INTERNATIONAL_DATE_FORMAT
} from 'src/common/constants';
import { isFeatureEnabledForActiveModule } from "src/helpers/module_helper";
import TableColumnCustomizer from "src/modules/3rd-party-management/components/TableColumnCustomizer";
import useTableColumnCustomizer from "src/modules/3rd-party-management/components/TableColumnCustomizer/CustomizerHook";
import { useHistory, useLocation } from "react-router-dom";
import Navigator from "src/components/Navigator";

const eudrDefaultCols = [
    'id',
    'name',
    'productName',
    'lotNumber',
    'internalProductId',
    'importFromCountry',
    'exportToCountry',
    'importDate',   
    'metricTonnes',
    'hsCode',
    'knCode',
    'ddsReferenceNumber',
    'statementValidation',
    // 'containerNumber',
    // 'shipmentNumber',
    'actions'
];

const Products = ({
    t,
    Organization
}) => {
    const [filters, setFilters] = useState({
        pageIndex: 1,
        pageSize: 25,
        sortField: null,
        sortOrder: null
    });

    const history = useHistory();
    const location = useLocation();

    const dateUtils = new DateUtils();

    const {
        isFetching: productsAreFetching,
        isFetched: productsFetched,
        data: products
    } = useQuery({
        queryKey: [
            '3rd-party-management-fetch-supplier-eudr-products',
            Organization.id,
            filters
        ],
        queryFn: async () => {
            const service = EudrDeclarationService.getInstance();

            return await service.list(Organization.id, filters);
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: () => {
            toast(t('An error occurred while fetching products list.'), {
                type: 'error'
            });
        }
    });

    const handleFetchCountriesQuery = useQuery({
        queryKey: ['3rd-party-management-fetch-countries-list'],
        queryFn: async () => {
            const service = CommonService.getInstance();

            return await service.fetchCountries();
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: () => {
            toast(t('An error occurred while fetching locations.'), {
                type: 'error'
            });
        }
    });

    const eudrAllColumns = useMemo(() => {
        return [
            {
                dataField: "id",
                text: t("ID"),
                sort: true,
                style: {
                    width: '80px'
                }
            },
            {
                dataField: "name",
                text: t("NACE Code"),
                sort: true,
                style: {
                    width: '200px'
                },
                formatter: (cellContent) => {
                    return `${cellContent.class} - ${t(cellContent.name)}`
                }
            },
            {
                dataField: "productName",
                text: t("Product Name"),
                sort: true,
                formatter: (_cell, { product_name }) => {
                    return product_name
                }
            },
            {
                dataField: "lotNumber",
                text: t("Lot Number"),
                sort: true,
                formatter: (_cell, { lot_number }) => {
                    return lot_number
                }
            },
            {
                dataField: "internalProductId",
                text: t("Internal Product ID"),
                sort: true,
                formatter: (_cell, { internal_product_id }) => {
                    return internal_product_id
                }
            },
            {
                dataField: "importDate",
                text: t("Date of Import"),
                sort: true,
                formatter: (_cellContent, { import_date }) => {
                    return (
                        dateUtils.convertDateToDate(import_date, INTERNATIONAL_DATE_FORMAT)
                    )
                }
            },
            {
                dataField: "importFromCountry",
                text: t("Import Country"),
                sort: true,
                formatter: (_cellContent, { import_from_country }) => {
                    return t(import_from_country?.name)
                }
            },
            {
                dataField: "exportDate",
                text: t("Date of Export"),
                sort: true,
                formatter: (_cellContent, { export_date }) => {
                    return (
                        dateUtils.convertDateToDate(export_date, INTERNATIONAL_DATE_FORMAT)
                    )
                }
            },
            {
                dataField: "exportToCountry",
                text: t("Export Country"),
                sort: true,
                formatter: (_cellContent, { export_to_country }) => {
                    return t(export_to_country?.name)
                }
            },
            {
                dataField: "metricTonnes",
                text: t("Quantity (Metric Tonnes)"),
                sort: true,
                formatter: (_cellContent, { metric_tonnes }) => {
                    return metric_tonnes
                }
            },
            {
                dataField: "originCountry",
                text: t("Country of Origin"),
                sort: true,
                formatter: (_cellContent, { origin_country }) => {
                    return t(origin_country?.name)
                }
            },
            {
                dataField: "ddsDeclrationType",
                text: t("DDS Declaration Type"),
                sort: false,
                formatter: (_cellContent, { dds_declration_type }) => {
                    return t(DDSTypes[dds_declration_type]?.title)
                }
            },
            ...(isFeatureEnabledForActiveModule('PPWR') ? [{
                dataField: "is_ppwr",
                text: t("PPWR Product"),
                sort: true,
                formatter: (cellContent) => {
                    if (!cellContent) {
                        return (
                            <span>
                                <i className="ri-close-line me-1 text-danger align-middle font-size-18" />
                                {t('No')}
                            </span>
                        )
                    }
                    return (
                        <span>
                            <i className="ri-checkbox-fill me-1 text-success align-middle font-size-18" />
                            {t('Yes')}
                        </span>
                    )
                }
            }] : []),
            {
                dataField: "ddsReferenceNumber",
                text: t("DDS Reference Number"),
                sort: true,
                formatter: (_cellContent, { dds_reference_number }) => {
                    return dds_reference_number
                }
            },
            {
                dataField: "ddsVerificationNumber",
                text: t("DDS Verification Number"),
                sort: true,
                formatter: (_cellContent, { dds_verification_number }) => {
                    return dds_verification_number
                }
            },
            {
                dataField: "statementValidation",
                text: t("DDS Statement Status"),
                sort: true,
                formatter: (cellContent, { dds_declaration_type }) => {
                    if (dds_declaration_type === 'provide_reference_numbers') {
                        if (cellContent === false) {
                            return (
                                <span>
                                    <i className="ri-close-line me-1 text-danger align-middle font-size-18" />
                                    {t('Invalid')}
                                </span>
                            )
                        }
                        else if (cellContent === true) {
                            return (
                                <span>
                                    <i className="ri-checkbox-fill me-1 text-success align-middle font-size-18" />
                                    {t('Valid')}
                                </span>
                            )
                        }
                        else {
                            return (
                                <span>
                                    {t('Validating')}...
                                </span>
                            )
                        }
                    }

                    return '-'
                }
            },
            {
                dataField: "geoStatus",
                text: t("Geo Status"),
                sort: false,
                formatter: (_cell, { geolocation_reference }) => {
                    if (geolocation_reference && geolocation_reference.geometry) {
                        return (
                            <span>
                                <i className="ri ri-check-line me-1 text-success font-size-20 align-middle" />
                                {t('Provided')}
                            </span>
                        )
                    }

                    return (
                        <span>
                            <i className="ri ri-close-line me-1 text-danger font-size-20 align-middle" />
                            {t('Not Provided')}
                        </span>
                    )
                }
            },
            {
                dataField: "hsCode",
                text: t("HS Code (6-digit)"),
                sort: true,
                formatter: (_cellContent, { hs_code }) => {
                    return hs_code
                }
            },
            {
                dataField: "knCode",
                text: t("KN Code (8-digit)"),
                sort: true,
                formatter: (_cellContent, { kn_code }) => {
                    return kn_code
                }
            },
            {
                dataField: "taricCode",
                text: t("TARIC Code (10-digit)"),
                sort: true,
                formatter: (_cellContent, { taric_code }) => {
                    return taric_code
                }
            },
            {
                dataField: "containerNumber",
                text: t("Container Number"),
                sort: true,
                formatter: (_cell, { container_number }) => {
                    return container_number
                }
            },
            {
                dataField: "shipmentNumber",
                text: t("Shipment Number"),
                sort: true,
                formatter: (_cell, { shipment_number }) => {
                    return shipment_number
                }
            },
            {
                dataField: "description",
                text: t('Description'),
                sort: false
            },
            {
                dataField: "actions",
                text: t("Action"),
                sort: false,
                style: {
                    width: '120px'
                },
                formatter: (_cellContent, row) => {
                    return (
                        <div className="actions">
                            <span id={`detail-btn-${row.id}`}>
                                <Button
                                    color="primary"
                                    aria-label={t('View Product Detail')}
                                    outline
                                    className="border-0"
                                    onClick={() => {
                                        history.push(`/third-party/3rd-party/products/${row.id}/show`);
                                    }}
                                >
                                    <i className="ri-eye-line font-size-20" />
                                </Button>

                                <UncontrolledTooltip
                                    placement="top"
                                    target={`detail-btn-${row.id}`}
                                >
                                    {t('View detailed product information')}.
                                </UncontrolledTooltip>
                            </span>

                            <span id={`geo-btn-${row.id}`}>
                                <Button
                                    color="primary"
                                    aria-label={t('View product details from GeoCleidian API')}
                                    outline
                                    className="border-0"
                                    onClick={() => {
                                        history.push(`/third-party/3rd-party/products/${row.id}/geo-detail`);
                                    }}
                                >
                                    <i className="ri-earth-line font-size-20" aria-hidden="true"></i>
                                </Button>

                                <UncontrolledTooltip
                                    placement="top"
                                    target={`geo-btn-${row.id}`}
                                >
                                    {t('View product origin and deforestation risk')}.
                                </UncontrolledTooltip>
                            </span>
                        </div>
                    )
                }
            }
        ]
    }, [
        t
    ]);

    const {
        selectedColumns,
        onColumnsChange
    } = useTableColumnCustomizer({
        defaultSelectedCols: eudrDefaultCols,
        uniqueIdentifier: 'tpdd-supplier-products-cols'
    });

    const visibleColumns = useMemo(() => {
        if(isFeatureEnabledForActiveModule('eudr_activation')) {
            return eudrAllColumns.filter((col) => selectedColumns.includes(col.dataField) || col.locked)
        }

        return [
            {
                dataField: "id",
                text: t("ID"),
                sort: true,
                style: {
                    width: '80px'
                }
            },
            {
                dataField: "name",
                text: t("NACE Code"),
                sort: true,
                style: {
                    width: '200px'
                },
                formatter: (cellContent) => {
                    return `${cellContent.class} - ${t(cellContent.name)}`
                }
            },
            {
                dataField: "product_name",
                text: t("Product Name"),
                sort: true
            },
            ...(isFeatureEnabledForActiveModule('PPWR') ? [{
                dataField: "is_ppwr",
                text: t("PPWR Product"),
                sort: true,
                style: {
                    width: '200px'
                },
                formatter: (cellContent) => {
                    if (!cellContent) {
                        return (
                            <span>
                                <i className="ri-close-line me-1 text-danger align-middle font-size-18" />
                                {t('No')}
                            </span>
                        )
                    }
                    return (
                        <span>
                            <i className="ri-checkbox-fill me-1 text-success align-middle font-size-18" />
                            {t('Yes')}
                        </span>
                    )
                }
            }] : []),
            {
                dataField: "description",
                text: t('Description'),
                sort: false,
                style: {
                    width: '200px'
                }
            }
        ]
    }, [
        t,
        eudrAllColumns,
        selectedColumns
    ]);

    const NoDataIndication = () => {
        if (productsFetched && !products?.length) {
            return (
                <div className="alert m-0" role="alert">
                    <p className="text-center mb-0">
                        {t(EMPTY_LIST)}
                    </p>
                </div>
            )
        }

        return <></>
    }

    const handleTableFilterChange = useCallback((newFilters) => {
        setFilters((currentState) => {
            return {
                ...currentState,
                ...newFilters,
                pageIndex: 1
            }
        })
    }, []);

    const handleTableChange = (type, options) => {
        const {
            page,
            sizePerPage,

            sortField,
            sortOrder
        } = options;

        switch(type){
            case 'pagination':
                    setFilters({
                        ...filters,
                        pageIndex   :   page,
                        pageSize    :   sizePerPage
                    });
                break;
            case 'sort':
                    setFilters({
                        ...filters,
                        sortField   :   sortField,
                        sortOrder   :   sortOrder.toUpperCase()
                    })
                break;
            default:
                return false;
        }
    }

    const isEudrEnabled = isFeatureEnabledForActiveModule('eudr_activation');

    return (
        <div className='page-content'>
            <Container fluid>
                <Row>
                    <Col lg="12" className="mb-4">
                        <Navigator
                            pageTitle="Products"
                            breadCrumbs={[
                                {
                                    title: 'SIP',
                                    link: '/third-party/3rd-party/home'
                                },
                                {
                                    title: 'Products',
                                    link: '#'
                                }
                            ]}
                        />
                    </Col>
                </Row>
                <Row>
                    <Col sm='12'>
                        <div
                            className='d-flex justify-content-between flex-row-reverse mb-3 mt-3'
                        >
                            <div className="d-flex justify-content-end">
                                <FilteringComponent
                                    onChange={handleTableFilterChange}
                                    countries={handleFetchCountriesQuery}
                                />
                            </div>

                            {isEudrEnabled && (
                                <TableColumnCustomizer 
                                    allColumns={eudrAllColumns}
                                    visibleColumns={selectedColumns}
                                    onChange={onColumnsChange}
                                />
                            )}
                        </div>
                    </Col>
                    <Col sm='12'>
                        <Card>
                            <CardBody>
                                <PaginationProvider
                                    pagination={
                                        paginationFactory({
                                            custom: true,
                                            page: filters.pageIndex,
                                            sizePerPage: filters.pageSize,
                                            totalSize: products?.itemsCount || 0,
                                            withFirstAndLast: true,
                                            alwaysShowAllBtns: true,
                                            prePageText: <span><i className="ri-arrow-left-s-line"></i> {t('Back')}</span>,
                                            nextPageText: <span>{t('Next')} <i className="ri-arrow-right-s-line"></i></span>,
                                            prePageTitle: t('Pre page'),
                                            firstPageTitle: t('First page'),
                                            lastPageTitle: t('Last page'),
                                            showTotal: false,
                                            paginationTotalRenderer: (from, to, size) => {
                                                return (
                                                    <span className="react-bootstrap-table-pagination-total">
                                                        {`${t('Total Items')} ${size}`}
                                                    </span>
                                                )
                                            },
                                            hideSizePerPage: false,
                                            sizePerPageList: [
                                                {
                                                    text: '25',
                                                    value: 25
                                                },
                                                {
                                                    text: '50',
                                                    value: 50
                                                }
                                            ]
                                        })
                                    }>
                                    {
                                        ({
                                            paginationProps,
                                            paginationTableProps
                                        }) => (
                                            <>
                                                <Row>
                                                    <Col sm="12">
                                                        <BootstrapTable
                                                            remote={{
                                                                pagination: true,
                                                                filter: false,
                                                                sort: true,
                                                                cellEdit: false,
                                                                search: false
                                                            }}
                                                            loading={productsAreFetching}
                                                            overlay={overlayFactory({ spinner: <Spinner animation="border" variant="primary" size="md" />, text: `${t("Loading")}...` })}
                                                            onTableChange={handleTableChange}
                                                            defaultSorted={[]}
                                                            keyField={"id"}
                                                            responsive={true}
                                                            bordered={false}
                                                            data={products?.products || []}
                                                            striped={true}
                                                            columns={visibleColumns}
                                                            wrapperClasses="table-responsive"
                                                            classes={"table tpdd-table"}
                                                            headerWrapperClasses={"thead-light"}
                                                            noDataIndication={() => <NoDataIndication />}
                                                            {...paginationTableProps}
                                                        />
                                                    </Col>
                                                </Row>

                                                <Row>
                                                    <Col sm="12">
                                                        <div className="tpdd-pagination-style-1">
                                                            <PaginationTotalStandalone
                                                                {...paginationProps}
                                                            />

                                                            <div>
                                                                <PaginationListStandalone
                                                                    {...paginationProps}
                                                                />

                                                                <SizePerPageDropdownStandalone
                                                                    {...paginationProps}
                                                                />
                                                            </div>
                                                        </div>
                                                    </Col>
                                                </Row>
                                            </>
                                        )
                                    }

                                </PaginationProvider>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </div>
    )
};

const mapStatetoProps = (state) => {
    const { Organization } = state;

    return {
        Organization
    };
};

export default withNamespaces()(
    connect(mapStatetoProps)(
        Products
    )
);